// Money › Receipts (prototype 30-receipts.js receiptsPage + the receipt drawer, 32-pdf.js pdfCard; spec 08 R-20 to R-22, R-25a).
// Read-only for now: the list, search and filters, and the drawer with the stored PDF (spec 06 6-5: a 60-second
// link from receipt_document_link, written to the audit log), the customer email and the history.
// Void, Email again, New receipt, bulk and saved views come next; the money journey of each line comes with phase 7.
import { Money } from '@xr/ui';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { plain } from '../lib/errors';
import { L, N, fDate, loc } from '../lib/prefs';
import { type Doc, type Filter, type Kind, type Line, type Mail, type Receipt, docFor, inFilter, mailWord, matches, paidBy, sumOf, verifyLink } from '../lib/receipts';
import { can, useApp } from '../lib/session';
import { type VoidOptions, blockText, openEmail, openVoid } from './receipt-actions';
import { rpc, sb } from '../lib/supabase';
import { Avatar, Btn, Empty, IBtn, Ic, Note, PageHead, tClose } from '../ui/kit';
import { FSel, Pill, SearchBox, Table } from '../ui/table';
import { toast } from '../ui/toast';

const KIND: Record<Kind, [string, string, string]> = { cash: ['Cash', 'نقداً', 'cash'], cheque: ['Cheque', 'شيك', 'cheque'], transfer: ['Bank transfer', 'تحويل بنكي', 'bank'], card: ['Card', 'بطاقة', 'card'] };
const TZ = 'Asia/Dubai';
const fDay = (t: string) => new Date(t).toLocaleDateString(loc(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
const fTime = (t: string) => new Date(t).toLocaleTimeString(loc(), { hour: '2-digit', minute: '2-digit', timeZone: TZ });
const fDT = (t: string) => fDay(t) + ' · ' + fTime(t);
const kb = (n: number) => (n >= 1_048_576 ? (n / 1_048_576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB');

type Names = { cust: Map<string, { name: string; email: string | null; trn: string | null }>; people: Map<string, string>; branches: Map<string, string> };
type Data = { receipts: Receipt[]; lines: Map<string, Line[]>; docs: Doc[]; names: Names };

function Status({ r }: { r: Receipt }) {
  if (r.status === 'voided') return <Pill tone="mute">{L('Void', 'ملغى')}</Pill>;
  if (r.review_state === 'open') return <Pill tone="info">{L('Review', 'مراجعة')}</Pill>;
  return <Pill tone="good">{L('Issued', 'صادر')}</Pill>;
}
const PaidBy = ({ lines }: { lines: Line[] }) => (
  <span className="pays">{paidBy(lines).map(([k, n]) => <span key={k} title={L(KIND[k][0], KIND[k][1])} className={'pay p-' + k}><Ic n={KIND[k][2]} />{n > 1 ? n : ''}</span>)}</span>
);

/** Opens or downloads the stored PDF: a 60-second grant from the database, then a 60-second signed link. */
async function openDoc(r: Receipt, kind: 'final' | 'void', download: boolean) {
  const win = download ? null : window.open('', '_blank'); // opened now, while the click still counts
  try {
    const g = await rpc<{ path: string; file_name: string }>('receipt_document_link', { p_receipt: r.id, p_kind: kind });
    const { data, error } = await sb.storage.from('documents').createSignedUrl(g.path, 60, download ? { download: g.file_name } : undefined);
    if (error || !data) throw error || new Error('network');
    if (win) win.location.href = data.signedUrl;
    else { const a = document.createElement('a'); a.href = data.signedUrl; a.download = g.file_name; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove(); }
    if (download) toast(L('Downloading ', 'جارٍ تنزيل ') + g.file_name, { icon: 'download' });
  } catch (e) {
    win?.close();
    const code = (e as { code?: string }).code;
    toast(code === 'pdf_not_ready' ? L('The PDF is being made. Try again in a minute.', 'يجري إعداد الملف. حاول بعد دقيقة.') : plain(e), { tone: 'bad' });
  }
}

function PdfCard({ r, docs }: { r: Receipt; docs: Doc[] }) {
  const d = docFor(r, docs);
  const name = r.display_number + (d?.kind === 'void' ? '-void' : '') + '.pdf';
  if (!d) return (
    <section className="pdf-card"><header><span className="pdf-ic"><Ic n="doc" /></span>
      <div><b><bdi dir="ltr">{r.display_number}.pdf</bdi></b><small>{L('Being made. It is usually ready within a minute.', 'يجري إعداده. عادةً يجهز خلال دقيقة.')}</small></div></header></section>
  );
  const meta = <>{[d.template_id.startsWith('L') ? L('Landscape classic', 'أفقي كلاسيكي') : L('A4 portrait', 'A4 عمودي'),
    L(d.pages + (d.pages === 1 ? ' page' : ' pages'), d.pages + ' صفحات')].join(' · ')} · <bdi dir="ltr">{kb(d.bytes)}</bdi></>;
  return (
    <section className="pdf-card" aria-label={L('Receipt PDF', 'ملف السند')}>
      <header>
        <span className="pdf-ic"><Ic n="doc" /></span>
        <div><b><bdi dir="ltr">{name}</bdi></b><small>{meta}{d.kind === 'void' ? ' · ' + L('VOID copy', 'نسخة ملغاة') : ''}</small></div>
        <div className="pdf-act">
          <Btn sm kind="ghost" icon="eye" onClick={() => void openDoc(r, d.kind, false)}>{L('Open', 'فتح')}</Btn>
          <Btn sm kind="ghost" icon="download" onClick={() => void openDoc(r, d.kind, true)}>{L('Download', 'تنزيل')}</Btn>
        </div>
      </header>
      {r.status === 'voided' && d.kind === 'void' ? (
        <p className="muted sm pdf-note">{L('This is the VOID copy the customer was emailed. The original stays in the archive.', 'هذه النسخة الملغاة التي أُرسلت للعميل. يبقى الأصل في الأرشيف.')}{' '}
          <button type="button" className="link" onClick={() => void openDoc(r, 'final', true)}>{L('Download the original', 'تنزيل الأصل')}</button></p>
      ) : <p className="muted sm pdf-note">{L('The PDF shows the receipt as issued. Deposits, bounces and other later events are not printed on it.', 'يعرض الملف السند كما صدر. لا تُطبع عليه الإيداعات والارتجاعات والأحداث اللاحقة.')}</p>}
    </section>
  );
}

function MailLine({ mail, r }: { mail: Mail | undefined; r: Receipt }) {
  const w = mailWord(mail);
  if (!mail) return <span className="muted">{r.customer_id ? L('No email sent', 'لم يُرسل بريد') : L('No email on file', 'لا يوجد بريد مسجل')}</span>;
  const tag: Record<typeof w.key, [string, string]> = {
    none: ['', ''], waiting: ['Waiting for the PDF', 'بانتظار الملف'], problem: ['Email waiting for the PDF', 'البريد بانتظار الملف'],
    sending: ['Sending', 'قيد الإرسال'], sent: ['Sent', 'أُرسل'], failed: ['Not delivered', 'لم يصل'],
  };
  const why: Partial<Record<typeof w.key, [string, string]>> = {
    waiting: ['It goes out with the PDF attached as soon as the PDF is ready, usually within a minute.', 'يُرسل مع الملف مرفقاً فور جاهزيته، عادةً خلال دقيقة.'],
    problem: ['The PDF couldn\'t be made yet. We keep trying and our team has been alerted; the email goes out with it, nothing is lost.', 'تعذّر إعداد الملف حتى الآن. نواصل المحاولة وتم تنبيه فريقنا؛ سيُرسل البريد معه ولن يضيع شيء.'],
    failed: ['The customer\'s mail server didn\'t accept it. Check the email address on the customer.', 'لم يقبله خادم بريد العميل. تحقق من عنوان البريد لدى العميل.'],
  };
  return (
    <span className="mail-state">
      <bdi>{mail.to.join(', ')}</bdi> <span className={'tag ' + w.tone}>{L(...tag[w.key])}</span>
      {mail.sent_at && w.key === 'sent' ? <small className="sub">{fDT(mail.sent_at)}</small> : null}
      {why[w.key] ? <small className="sub">{L(...why[w.key]!)}</small> : null}
      {mail.cc.length ? <small className="sub">{L('copy to ', 'نسخة إلى ')}<bdi>{mail.cc.join(', ')}</bdi></small> : null}
    </span>
  );
}

type Hist = { seq: number; at: string; action: string; actor_user_id: string | null; detail: Record<string, unknown> };
const ACTS: Record<string, [string, string]> = { 'receipt.issued': ['Issued', 'صدر'], 'receipt.voided': ['Voided', 'أُلغي'], 'receipt.downloaded': ['PDF opened or downloaded', 'فُتح الملف أو نُزّل'], 'receipt.emailed': ['Emailed to the customer', 'أُرسل للعميل'], 'receipt.reviewed': ['Marked reviewed', 'عُلّم كمراجع'] };

function Drawer({ r, data, onClose, onChanged }: { r: Receipt; data: Data; onClose: () => void; onChanged: () => void }) {
  const [mails, setMails] = useState<Mail[] | null>(null);
  const [vo, setVo] = useState<VoidOptions | null>(null);
  const [tick, setTick] = useState(0);
  const [hist, setHist] = useState<Hist[] | null>(null);
  const { names } = data;
  const lines = data.lines.get(r.id) || [];
  const c = r.customer_id ? names.cust.get(r.customer_id) : undefined;
  const fin = can('customers'); // owner, admin and finance, not read-only: the prototype's can('fin')
  const changed = () => { setTick(t => t + 1); onChanged(); };
  useEffect(() => {
    let live = true;
    void rpc<Mail[]>('receipt_emails', { p_receipt: r.id }).then(m => live && setMails(m || []), () => live && setMails([]));
    if (fin && r.status === 'issued') void rpc<VoidOptions>('void_options', { p_receipt: r.id }).then(o => live && setVo(o), () => live && setVo(null));
    void sb.from('audit_log').select('seq,at,action,actor_user_id,detail').eq('object_type', 'receipt').eq('object_id', r.id).order('seq', { ascending: false }).limit(50)
      .then(({ data: h }) => live && setHist((h as Hist[]) || []));
    return () => { live = false; };
  }, [r.id, r.status, tick, fin]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.modal')) onClose(); };
    document.addEventListener('keydown', esc);
    document.querySelector<HTMLElement>('.drawer .d-head .ibtn')?.focus();
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  const link = verifyLink(r);
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); toast(L('Copied', 'تم النسخ'), { icon: 'link' }); }
    catch { toast(link, { icon: 'link' }); }
  };
  // the latest email (a resend included); for a voided receipt, the void email first
  const mail = (r.status === 'voided' ? (mails || []).filter(m => m.kind === 'void').slice(-1)[0] : undefined) || (mails || []).slice(-1)[0];
  const kv: [ReactNode, ReactNode][] = [
    [L('Customer', 'العميل'), <>{c ? <bdi>{c.name}</bdi> : '—'}{c?.trn ? <small className="sub">TRN {c.trn}</small> : null}</>],
    [L('Signed by', 'وقّع'), r.signer_name ? <bdi>{r.signer_name}</bdi> : '—'],
    [L('Issued by', 'أصدره'), <span className="who"><Avatar name={names.people.get(r.created_by) || '?'} size="xs" /><bdi>{names.people.get(r.created_by) || '—'}</bdi></span>],
    [L('Branch', 'الفرع'), names.branches.get(r.branch_id) || '—'],
    [L('Date', 'التاريخ'), <>{fDT(r.issued_at)}{r.captured_offline && r.synced_at ? <small className="sub">{L('No signal: numbered when it synced at ', 'دون إشارة: رُقّم عند المزامنة ') + fTime(r.synced_at)}</small> : null}</>],
    [L('Email', 'البريد'), mails ? <MailLine mail={mail} r={r} /> : <span className="muted">…</span>],
    ...(r.refs?.length ? [[L('References', 'المراجع'), r.refs.join(', ')] as [ReactNode, ReactNode]] : []),
  ];
  const box = document.getElementById('layer');
  if (!box) return null;
  return createPortal(
    <>
      <div className="scrim enter" onClick={onClose} />
      <aside className="drawer wide enter" role="dialog" aria-modal="true" aria-label={L('Receipt ', 'سند ') + r.display_number}>
        <header className="d-head">
          <div className="d-title"><small>{L('Receipt', 'سند')}</small><b className="mono">№ {r.display_number}</b></div>
          <IBtn icon="x" title={tClose()} onClick={onClose} />
        </header>
        <div className="d-body">
          <div className="d-pills"><Status r={r} /><span className="amt big"><Money value={Number(r.total)} /></span></div>
          {r.status === 'voided' ? (
            <Note tone="bad" icon="ban"><span><b>{L('Voided', 'ملغى')}</b>{r.voided_by ? <> {L('by', 'بواسطة')} <bdi>{names.people.get(r.voided_by) || '—'}</bdi></> : null}{r.voided_at ? ' · ' + fDT(r.voided_at) : ''}</span>{r.void_reason ? <span>“<bdi>{r.void_reason}</bdi>”</span> : null}</Note>
          ) : null}
          <PdfCard r={r} docs={data.docs} />
          <h3 className="d-h">{L('Payments', 'الدفعات')}</h3>
          <div className="lines">{lines.map(l => (
            <div className="line" key={l.id}><div className="ln-h">
              <span className={'pay p-' + l.kind}><Ic n={KIND[l.kind][2]} /></span>
              <div className="ln-t"><b>{L(KIND[l.kind][0], KIND[l.kind][1])}{l.cheque_no ? ' · ' + l.cheque_no : ''}</b>
                <small>{l.kind === 'cheque' ? [l.bank_code, l.cheque_date ? L('due ', 'يستحق ') + fDate(l.cheque_date + 'T09:00:00') : ''].filter(Boolean).join(' · ') : l.transfer_ref || (l.card_last4 ? '•••• ' + l.card_last4 : '')}</small></div>
              <b className="ln-a"><span className="amt"><Money value={Number(l.amount)} /></span></b>
            </div></div>
          ))}</div>
          {r.narrative ? <><h3 className="d-h">{L('Being payment for', 'وذلك عن')}</h3><p className="lead"><bdi>{r.narrative}</bdi></p></> : null}
          <h3 className="d-h">{L('Details', 'التفاصيل')}</h3>
          <dl className="kv">{kv.map(([k, v], i) => <div key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
          {hist && hist.length ? <>
            <h3 className="d-h">{L('History', 'السجل')}</h3>
            <ul className="feed">{hist.map(h => (
              <li key={h.seq}>{h.actor_user_id ? <Avatar name={names.people.get(h.actor_user_id) || '?'} size="sm" /> : <span className="av sys"><Ic n="shield" /></span>}
                <div><span>{ACTS[h.action] ? L(...ACTS[h.action]!) : h.action}{h.detail?.kind === 'void' ? ' · ' + L('VOID copy', 'نسخة ملغاة') : ''}{h.actor_user_id ? <> · <bdi>{names.people.get(h.actor_user_id) || '—'}</bdi></> : null}</span><small>{fDT(h.at)}</small></div></li>
            ))}</ul>
          </> : null}
        </div>
        <footer className="d-foot">
          <Btn kind="ghost" icon="link" disabled={!link} why={L('The check link comes with the PDF', 'رابط التحقق يأتي مع الملف')} onClick={() => void copy()}>{L('Copy check link', 'نسخ رابط التحقق')}</Btn>
          {fin && r.status === 'issued' ? <Btn kind="ghost" icon="mail" onClick={() => openEmail({ id: r.id, number: r.display_number, customer: c?.email || null, cc: mail?.cc || [], voided: false, onDone: changed })}>{L('Resend', 'إعادة إرسال')}</Btn> : null}
          <span className="sp" />
          {fin && r.status === 'issued' ? <Btn kind="danger-ghost" icon="ban" disabled={!vo?.can_void}
            why={vo ? blockText(vo.blocked) || L('Only finance, admins and owners can void', 'الإلغاء للمالية والمسؤولين والمالك') : L('Checking…', 'جارٍ التحقق…')}
            onClick={() => openVoid(r.id, r.display_number, changed)}>{L('Void', 'إلغاء')}</Btn> : null}
        </footer>
      </aside>
    </>,
    box,
  );
}

export function ReceiptsPage() {
  const { co, me } = useApp();
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [f, setF] = useState<Filter>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const id = co?.id;
  const reload = useCallback(async () => {
    if (!id) return;
    const [rs, ls, ds, cu, pe, br] = await Promise.all([
      sb.from('receipts').select('id,display_number,status,issued_at,total,currency,customer_id,created_by,branch_id,narrative,refs,signer_name,captured_offline,synced_at,review_state,voided_at,voided_by,void_reason,public_id,location')
        .eq('tenant_id', id).in('status', ['issued', 'voided']).order('issued_at', { ascending: false }),
      sb.from('receipt_lines').select('id,receipt_id,kind,amount,position,cheque_no,bank_code,cheque_date,transfer_ref,card_last4').eq('tenant_id', id),
      sb.from('receipt_documents').select('receipt_id,kind,pages,bytes,template_id,rendered_at').eq('tenant_id', id),
      sb.from('customers').select('id,name_en,name_ar,email,trn').eq('tenant_id', id),
      sb.from('user_profiles').select('user_id,name'),
      sb.from('branches').select('id,name_en,name_ar').eq('tenant_id', id),
    ]);
    if (rs.error) return setFailed(true);
    const lines = new Map<string, Line[]>();
    for (const l of (ls.data || []) as Line[]) lines.set(l.receipt_id, [...(lines.get(l.receipt_id) || []), l].sort((a, b) => a.position - b.position));
    setData({
      receipts: rs.data as Receipt[], lines, docs: (ds.data || []) as Doc[],
      names: {
        cust: new Map((cu.data || []).map(x => [x.id, { name: N(x.name_en, x.name_ar), email: x.email, trn: x.trn }])),
        people: new Map((pe.data || []).map(x => [x.user_id, x.name || ''])),
        branches: new Map((br.data || []).map(x => [x.id, N(x.name_en, x.name_ar)])),
      },
    });
  }, [id]);
  useEffect(() => { void reload(); }, [reload]);
  const close = useCallback(() => setOpenId(null), []);
  const team = me?.role === 'supervisor';
  const head = (n?: number) => <PageHead title={L('Receipts', 'السندات')}
    sub={team ? L('Receipts issued by your team.', 'سندات فريقك.') : n == null ? '' : L(`${n} receipts. Search by number, customer, cheque number or invoice.`, `${n} سنداً. ابحث بالرقم أو العميل أو رقم الشيك أو الفاتورة.`)} />;
  if (failed) return <>{head()}<div className="card"><Empty icon="alert" title={L('Something went wrong on this page', 'حدث خطأ في هذه الصفحة')} body={plain(null)} /></div></>;
  if (!data) return head();
  const { names } = data;
  const nm = { cust: (x: string | null) => (x ? names.cust.get(x)?.name || '' : ''), person: (x: string) => names.people.get(x) || '' };
  const rows = data.receipts.filter(r => inFilter(r, data.lines.get(r.id) || [], f) && matches(r, data.lines.get(r.id) || [], q, nm));
  const people = [...new Set(data.receipts.map(r => r.created_by))].map(p => [p, nm.person(p) || '—'] as [string, string]);
  const multiBranch = new Set(data.receipts.map(r => r.branch_id)).size > 1;
  const set = (k: keyof Filter) => (v: string) => setF({ ...f, [k]: v || undefined });
  const filtered = Object.values(f).some(Boolean) || !!q;
  const open = data.receipts.find(r => r.id === openId);
  return (
    <>
      {head(data.receipts.length)}
      <div className="toolbar">
        <SearchBox id="receipts" value={q} onChange={setQ} ph={L('Search number, customer, cheque, invoice…', 'ابحث بالرقم أو العميل أو الشيك أو الفاتورة…')} />
        <label className={'fsel ' + (f.from ? 'on' : '')}><span>{L('From', 'من')}</span><input id="f-receipts-from" type="date" value={f.from || ''} onChange={e => set('from')(e.target.value)} aria-label={L('From', 'من')} /></label>
        <label className={'fsel ' + (f.to ? 'on' : '')}><span>{L('To', 'إلى')}</span><input id="f-receipts-to" type="date" value={f.to || ''} onChange={e => set('to')(e.target.value)} aria-label={L('To', 'إلى')} /></label>
        <FSel id="f-receipts-by" label={L('Issued by', 'أصدره')} value={f.by || ''} onChange={set('by')} opts={people} />
        {multiBranch ? <FSel id="f-receipts-branch" label={L('Branch', 'الفرع')} value={f.branch || ''} onChange={set('branch')} opts={[...names.branches].map(([k, v]) => [k, v])} /> : null}
        <FSel id="f-receipts-kind" label={L('Payment', 'الدفع')} value={f.kind || ''} onChange={set('kind')} opts={(Object.keys(KIND) as Kind[]).map(k => [k, L(KIND[k][0], KIND[k][1])])} />
        <FSel id="f-receipts-status" label={L('Status', 'الحالة')} value={f.status || ''} onChange={set('status')} opts={[['issued', L('Issued', 'صادر')], ['attention', L('Needs attention', 'يحتاج انتباه')], ['voided', L('Void', 'ملغى')]]} />
        {filtered ? <Btn sm kind="ghost" icon="x" onClick={() => { setF({}); setQ(''); }}>{L('Clear filters', 'مسح التصفية')}</Btn> : null}
      </div>
      <div className="sumbar"><span>{L(rows.length + ' receipts', rows.length + ' سنداً')}</span><b><span className="amt"><Money value={sumOf(rows)} /></span></b><span className="muted">{L('excluding void', 'دون الملغاة')}</span></div>
      <div className="card flush">
        <Table<Receipt> rows={rows} rowKey={r => r.id} stack
          empty={data.receipts.length ? undefined : <Empty icon="receipt" title={L('No receipts yet', 'لا سندات بعد')} body={L('Receipts issued on phones appear here within seconds.', 'تظهر السندات الصادرة من الهواتف هنا خلال ثوانٍ.')} />}
          cols={[
            { k: 'no', label: L('No.', 'الرقم'), cls: 'ga', sort: r => r.issued_at, html: r => <button type="button" className="link mono row-open" onClick={() => setOpenId(r.id)} aria-label={L('Open receipt ', 'فتح السند ') + r.display_number}><b>{r.display_number}</b></button> },
            { k: 'at', label: L('Date', 'التاريخ'), cls: 'gd', sort: r => r.issued_at, html: r => <><span className="nowrap">{fDay(r.issued_at)}</span><small className="sub">{fTime(r.issued_at)}</small></> },
            { k: 'cust', label: L('Customer', 'العميل'), cls: 'grow gc', sort: r => nm.cust(r.customer_id), html: r => <><span className="ell"><bdi>{nm.cust(r.customer_id) || '—'}</bdi></span><small className="sub ell">{r.narrative || ''}</small></> },
            { k: 'by', label: L('Issued by', 'أصدره'), cls: 'hide-md gx', sort: r => nm.person(r.created_by), html: r => <span className="who"><Avatar name={nm.person(r.created_by) || '?'} size="xs" /><span><bdi>{nm.person(r.created_by) || '—'}</bdi></span></span> },
            ...(multiBranch ? [{ k: 'br', label: L('Branch', 'الفرع'), cls: 'hide-lg gx', html: (r: Receipt) => names.branches.get(r.branch_id) || '—' }] : []),
            { k: 'pay', label: L('Paid by', 'طريقة الدفع'), cls: 'hide-sm gx', html: r => <PaidBy lines={data.lines.get(r.id) || []} /> },
            { k: 'amt', label: L('Amount', 'المبلغ'), cls: 'gb', num: true, sort: r => Number(r.total), html: r => <span className={'amt ' + (r.status === 'voided' ? 'strike' : '')}><Money value={Number(r.total)} /></span> },
            { k: 'st', label: L('Status', 'الحالة'), cls: 'ge', html: r => <Status r={r} /> },
          ]} />
      </div>
      {open ? <Drawer r={open} data={data} onClose={close} onChanged={() => void reload()} /> : null}
    </>
  );
}
